// Image.js - Updated with secure API calls and proper authentication integration
import { useEffect } from 'react';
import Application, { mountComponent, openDialog, closeDialog } from './Application';
import Authentication from './Authentication';
import { openLoginDialog } from './Login';

document.addEventListener('DOMContentLoaded', () => {
    // Mount the application to handle dialogs
    mountComponent('root', Application);
    mountComponent('root', Image);
});

// Modified handleThumbnailClick to use secure API calls
const handleThumbnailClick = async (entity, id, url, keywordsText) => {
    try {
        // Use secure API call with authentication
        const response = await Authentication.fetchWithAuthentication(
            `/api/image/${entity}?id=${id}&url=${encodeURIComponent(url)}`,
            {
                method: 'GET'
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Failed to update image');
        }

        const result = await response.json();

        // Update the original image source if it exists in the DOM
        const originalImg = document.querySelector(`img[id="${id}"]`);
        if (originalImg) {
            originalImg.src = url;
            originalImg.title = keywordsText;
        }
        closeDialog();
    } catch (error) {
        console.error(`Error updating image ${id}:`, error);

        // Handle authentication errors
        if (error.message.includes('Authentication')) {
            openLoginDialog();
        } else if (error.message.includes('Access denied')) {
            console.error('You do not have permission to perform this action', 'error');
        } else {
            console.error(`Failed to update image: ${error.message}`, 'error');
        }
    }
};


// Helper function to open login dialog

const Image = () => {
    useEffect(() => {
        // Check authentication state on component mount
        const handleAuthenticationStateChange = (event, data) => {
            if (event === 'logout') {
                updateImageInteractions();
            } else if (event === 'login') {
                updateImageInteractions();
            }
        };

        // Add auth state listener
        const removeListener = Authentication.addListener(handleAuthenticationStateChange);

        // Function to update image interactions based on auth state
        const updateImageInteractions = () => {
            const images = document.querySelectorAll('img');
            images.forEach(img => {
                // Remove existing styles
                img.style.border = '';
                img.style.transition = '';
                img.style.cursor = '';

                // Add visual feedback for authenticated users with proper roles
                if (Authentication.isSessionActive() && Authentication.hasAnyRole(['Superior', 'Admin'])) {
                    img.style.cursor = 'pointer';
                    img.style.border = '1px dotted transparent';
                    img.style.transition = 'border-color 0.3s ease';

                    // Remove existing event listeners to prevent duplicates
                    img.removeEventListener('mouseenter', img._mouseEnterHandler);
                    img.removeEventListener('mouseleave', img._mouseLeaveHandler);

                    // Add new event listeners
                    img._mouseEnterHandler = () => {
                        img.style.borderColor = 'var(--title)';
                    };
                    img._mouseLeaveHandler = () => {
                        img.style.borderColor = 'transparent';
                    };

                    img.addEventListener('mouseenter', img._mouseEnterHandler);
                    img.addEventListener('mouseleave', img._mouseLeaveHandler);
                }
            });
        };

        const handleImageClick = async (e) => {
            const img = e.target;

            if (!img.alt) return;

            e.preventDefault();
            e.stopPropagation();

            // Check if user is authenticated before allowing image selection
            if (!Authentication.isSessionActive()) {
                openLoginDialog();
                return;
            }

            // Check if user has required role
            if (!Authentication.hasAnyRole(['Superior', 'Admin'])) {
                console.error('You do not have permission to change images', 'error');
                return;
            }

            // Mark the image as active for later reference
            img.setAttribute('data-active', 'true');

            // Open the thumbnail viewer dialog
            openDialog(
                'viewer',
                {
                    keyword: img.title
                },
                'Selecciona una imagen',
                "medium"
            );
        };

        // Listen for the custom imageSelected event from Viewer
        const handleImageSelected = (event) => {
            const { imageUrl, keywordsText } = event.detail;

            // Find the currently clicked image
            const img = document.querySelector('img[data-active="true"]');
            if (img) {
                console.log(img.id);
                const entity = document.getElementById('entity')?.value || 'communication';
                handleThumbnailClick(entity, img.id, imageUrl, keywordsText);
                // Remove the active marker
                img.removeAttribute('data-active'); // Corrected from currentImg
            }
        };

        // Add event listener for image selection from Viewer
        document.addEventListener('imageSelected', handleImageSelected);


        // Add click listeners to all images
        const images = document.querySelectorAll('img');
        images.forEach(img => {
            img.addEventListener('click', handleImageClick);
        });

        // Initial update of image interactions
        updateImageInteractions();



        // Cleanup function
        return () => {
            images.forEach(img => {
                img.removeEventListener('click', handleImageClick);
                img.removeEventListener('mouseenter', img._mouseEnterHandler);
                img.removeEventListener('mouseleave', img._mouseLeaveHandler);
                img.style.cursor = '';
                img.style.border = '';
                img.style.transition = '';
            });
            document.removeEventListener('imageSelected', handleImageSelected);
            removeListener();
        };
    }, []);

    return null;
};

export default Image;